import { config } from './config'

type AccessToken = string
type Methods = 'POST' | 'GET' | 'PATCH' | 'DELETE'

export interface CommerceClient {
  get: (endpoint: string) => Promise<unknown>
  post: (endpoint: string, data: unknown) => Promise<unknown>
  patch: (endpoint: string, data: unknown) => Promise<unknown>
  delete: (endpoint: string) => Promise<unknown>
}

export const createClient = (accessToken?: AccessToken): CommerceClient => {
  if (!accessToken) {
    throw new Error('Unauthorized. Missing access token.')
  }

  const request = async (method: Methods, endpoint: string, data?: unknown) => {
    const url = `${config.endpoint}/api${endpoint}`

    const headers = {
      Authorization: `Bearer ${accessToken}`,
      'Content-Type': 'application/vnd.api+json',
      Accept: 'application/vnd.api+json',
    }

    const options: RequestInit = {
      method,
      headers,
    }

    if (data) {
      options.body = JSON.stringify(data)
    }

    try {
      const response = await fetch(url, options)

      if (response.status === 204) {
        return null
      }

      const json = await response.json()

      if (!response.ok) {
        const errorDetail = json.errors?.[0]?.detail || 'An error occurred'
        throw new Error(`Error ${response.status}: ${errorDetail}`)
      }

      return json
    } catch (error) {
      console.error(`Error in ${method} ${url}:`, error)
      throw error
    }
  }

  const get = (endpoint: string) => request('GET', endpoint)

  const post = (endpoint: string, data: unknown) => request('POST', endpoint, data)

  const patch = (endpoint: string, data: unknown) => request('PATCH', endpoint, data)

  const remove = (endpoint: string) => request('DELETE', endpoint)

  return {
    get,
    post,
    patch,
    delete: remove,
  }
}

export const getBaseCommerceLayerApiUrl = () => {
  return config.endpoint
}
